'use client';

import { useEffect } from 'react';
import { getCookieConsent, type CookieConsent } from '@/lib/cookie-consent';

// Type definitions for Google Analytics gtag function
interface WindowWithGtag extends Window {
  gtag?: (
    command: 'consent',
    action: 'update',
    params: {
      analytics_storage?: 'granted' | 'denied';
      ad_storage?: 'granted' | 'denied';
    }
  ) => void;
}

/**
 * Client component that manages Google Analytics consent updates
 * This runs on the client side to handle cookie consent changes
 */
export function GoogleAnalyticsConsent() {
  useEffect(() => {
    // Check initial consent
    const consent = getCookieConsent();
    const hasConsent = consent?.analytical === true;

    if (hasConsent) {
      // Update GA consent state immediately
      const windowWithGtag = window as WindowWithGtag;
      if (typeof window !== 'undefined' && windowWithGtag.gtag) {
        windowWithGtag.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
    }

    // Listen for consent updates
    const handleConsentUpdate = (event: CustomEvent<CookieConsent | null>) => {
      const updatedConsent = event.detail;
      const windowWithGtag = window as WindowWithGtag;
      if (typeof window !== 'undefined' && windowWithGtag.gtag) {
        if (updatedConsent?.analytical === true) {
          // Update GA consent state
          windowWithGtag.gtag('consent', 'update', {
            analytics_storage: 'granted',
          });
        } else if (updatedConsent?.analytical === false) {
          // Update GA consent state
          windowWithGtag.gtag('consent', 'update', {
            analytics_storage: 'denied',
          });
        }
      }
    };

    window.addEventListener('cookie-consent-updated', handleConsentUpdate as EventListener);

    return () => {
      window.removeEventListener('cookie-consent-updated', handleConsentUpdate as EventListener);
    };
  }, []);

  return null;
}

